// MF-554 — Order of the Analytics menu: drag the buttons into place and save the whole order at
// once (replaces typing position numbers). Used in Settings › Analytics charts and the Board Builder.
// Native HTML5 drag (react-beautiful-dnd loses its draggables inside a MUI Dialog under React 18);
// the arrows do the same from the keyboard.
import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useSnackbar } from 'notistack'
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Skeleton,
  Typography
} from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  getAnalyticsCharts,
  reorderAnalyticsCharts
} from '../../../../../../services/tenant/analytics-chart.service'
import { updateAnalyticsChartsList } from '../../../../../../store/actions/tenant.actions'
import { reorderMenuAnalytics } from '../../../../../../store/actions/menuActions'

const ENGINE_LABEL = {
  canvas: 'Board',
  'bi-custom': 'Maestro chart',
  'analytics-curriculum': 'Curriculum',
  tableau: 'Tableau'
}

const byPosition = (a, b) => Number(a.position) - Number(b.position)

/**
 * @param {{ open: boolean, onClose: Function, highlightId?: string }} props
 *   highlightId: a button to point out (the Board being edited).
 */
const MenuOrderDialog = ({ open, onClose, highlightId }) => {
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()
  const [rows, setRows] = useState(null)
  const [saving, setSaving] = useState(false)
  const [dirty, setDirty] = useState(false)
  const [dragIndex, setDragIndex] = useState(null)

  useEffect(() => {
    if (!open) return undefined
    setRows(null)
    setDirty(false)
    getAnalyticsCharts()
      .then((res) => setRows([...(res?.data || [])].sort(byPosition)))
      .catch((err) => {
        setRows([])
        enqueueSnackbar(err.message || 'The Analytics menu could not be loaded', { variant: 'error' })
      })
  }, [open, enqueueSnackbar])

  const move = (from, to) => {
    if (to < 0 || to >= rows.length || from === to) return
    const next = [...rows]
    const [it] = next.splice(from, 1)
    next.splice(to, 0, it)
    setRows(next)
    setDirty(true)
  }

  const save = async () => {
    setSaving(true)
    try {
      const res = await reorderAnalyticsCharts(rows.map((r) => r._id))
      const pos = new Map((res?.data || []).map((p) => [String(p._id), p.position]))
      const list = await getAnalyticsCharts()
      dispatch(updateAnalyticsChartsList(list?.data || []))
      dispatch(reorderMenuAnalytics(rows.map((r) => r.code)))
      enqueueSnackbar('Analytics menu order saved', { variant: 'success' })
      onClose(pos)
    } catch (err) {
      enqueueSnackbar(err.message || 'The order could not be saved', {
        variant: 'error',
        autoHideDuration: 8000
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} maxWidth='sm' fullWidth>
      <DialogTitle sx={{ pb: 0.5 }}>Order of the Analytics menu</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 2 }}>
          Drag the buttons into the order they should appear in the menu (or use the arrows). Everyone sees
          the new order; people who are logged in see it after they log in again.
        </Typography>
        {rows === null ? (
          [0, 1, 2, 3, 4].map((i) => <Skeleton key={i} height={44} />)
        ) : (
          <Box onDragOver={(e) => e.preventDefault()}>
            {rows.map((row, index) => (
              <Box
                key={row._id}
                draggable={!saving}
                onDragStart={(e) => {
                  e.dataTransfer.effectAllowed = 'move'
                  e.dataTransfer.setData('text/plain', String(row._id))
                  setDragIndex(index)
                }}
                onDragEnter={() => {
                  // live reorder while dragging over another row
                  if (dragIndex === null || dragIndex === index) return
                  move(dragIndex, index)
                  setDragIndex(index)
                }}
                onDragEnd={() => setDragIndex(null)}
                onDrop={(e) => e.preventDefault()}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  px: 1.5,
                  py: 1,
                  mb: 0.75,
                  borderRadius: '10px',
                  border: '1px solid',
                  borderColor: row._id === highlightId ? 'primary.main' : 'divider',
                  bgcolor: dragIndex === index ? 'action.selected' : 'background.paper',
                  opacity: dragIndex === index ? 0.85 : 1,
                  cursor: saving ? 'default' : 'grab'
                }}
              >
                <Box sx={{ color: 'text.disabled', display: 'flex' }} aria-hidden>
                  <FontAwesomeIcon icon={['fas', 'grip-dots-vertical']} />
                </Box>
                <Typography sx={{ fontSize: 12, color: 'text.disabled', width: 20, textAlign: 'right' }}>
                  {index + 1}
                </Typography>
                <Typography noWrap sx={{ fontSize: 14, flex: 1, minWidth: 0 }}>
                  {row.name}
                </Typography>
                <Chip
                  size='small'
                  variant={row.engine === 'canvas' ? 'filled' : 'outlined'}
                  color={row.engine === 'canvas' ? 'primary' : 'default'}
                  label={ENGINE_LABEL[row.engine || 'tableau'] || row.engine}
                  sx={{ height: 20, fontSize: 11 }}
                />
                <Box sx={{ display: 'flex' }}>
                  <Button
                    size='small'
                    sx={{ minWidth: 28, px: 0.5 }}
                    disabled={saving || index === 0}
                    onClick={() => move(index, index - 1)}
                    aria-label={`Move ${row.name} up`}
                  >
                    <FontAwesomeIcon icon={['fal', 'chevron-up']} />
                  </Button>
                  <Button
                    size='small'
                    sx={{ minWidth: 28, px: 0.5 }}
                    disabled={saving || index === rows.length - 1}
                    onClick={() => move(index, index + 1)}
                    aria-label={`Move ${row.name} down`}
                  >
                    <FontAwesomeIcon icon={['fal', 'chevron-down']} />
                  </Button>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={() => onClose()} disabled={saving}>
          Cancel
        </Button>
        <Button variant='contained' onClick={save} disabled={!dirty || saving || !rows?.length}>
          {saving ? 'Saving…' : 'Save order'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default MenuOrderDialog
